import { useQuery } from "@tanstack/react-query";
import { fetchStoryItemSql } from "../services/storyCanvas/storyItemSqlApi";
import { getStoryItemSqlErrorMessage } from "../services/storyCanvas/storyItemSqlMappers";
import type { StoryItemSql } from "../services/storyCanvas/storyItemSql.types";

/** Own key: the SQL changes whenever the chart does, so it is never shared with the item's other reads. */
export const storyItemSqlKey = (storyId: string, itemId: string) =>
  ["storyItemSql", storyId, itemId] as const;

export interface UseStoryItemSqlResult {
  /** `idle` until there is a chart to ask about and the caller wants it. */
  state: "idle" | "loading" | "error" | "ready";
  data: StoryItemSql | null;
  /** One plain line for `error`, otherwise null. */
  errorMessage: string | null;
}

/**
 * `GET /api/v2/stories/:storyId/items/:itemId/sql` for the chart in the View SQL dialog.
 * `enabled` is false until the dialog is open, so a closed header costs no request. No retry: a 404,
 * 405 or 409 is an answer, not a blip. The text is never logged.
 */
export const useStoryItemSql = (
  storyId: string,
  itemId: string | null,
  enabled = true,
): UseStoryItemSqlResult => {
  const active = enabled && Boolean(storyId) && Boolean(itemId);
  const query = useQuery({
    queryKey: storyItemSqlKey(storyId, itemId ?? ""),
    queryFn: () => fetchStoryItemSql(storyId, itemId as string),
    enabled: active,
    retry: false,
  });
  if (!active) return { state: "idle", data: null, errorMessage: null };
  if (query.isError) return { state: "error", data: null, errorMessage: getStoryItemSqlErrorMessage(query.error) };
  if (query.data) return { state: "ready", data: query.data, errorMessage: null };
  return { state: "loading", data: null, errorMessage: null };
};
